import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Star, Shield } from 'lucide-react';

const ComparisonTable = () => {
  const features = [
    { name: "No ads or distractions", hiveshare: true, youtube: false, dropbox: true, vimeo: "paid" },
    { name: "Family privacy controls", hiveshare: true, youtube: false, dropbox: false, vimeo: false },
    { name: "Mobile app for parents", hiveshare: true, youtube: true, dropbox: true, vimeo: false },
    { name: "Upload from phone", hiveshare: true, youtube: true, dropbox: true, vimeo: true },
    { name: "Built for activity centers", hiveshare: true, youtube: false, dropbox: false, vimeo: false },
    { name: "Automatic organization", hiveshare: true, youtube: false, dropbox: false, vimeo: false },
    { name: "Secure sharing links", hiveshare: true, youtube: false, dropbox: true, vimeo: true },
    { name: "Download videos", hiveshare: true, youtube: false, dropbox: true, vimeo: "paid" },
    { name: "Unlimited storage", hiveshare: true, youtube: true, dropbox: false, vimeo: false },
    { name: "Simple pricing", hiveshare: true, youtube: true, dropbox: false, vimeo: false }
  ];

  const platforms = [
    {
      name: "HiveShare",
      key: "hiveshare",
      highlight: true,
      price: "$29/mo",
      description: "Built for activity centers",
      badge: "Recommended",
      color: "bg-blue-600"
    },
    {
      name: "YouTube/Band",
      key: "youtube", 
      highlight: false,
      price: "Free",
      description: "Social media platform",
      badge: null,
      color: "bg-red-600"
    },
    {
      name: "Dropbox",
      key: "dropbox",
      highlight: false,
      price: "$15+/mo",
      description: "File storage",
      badge: null,
      color: "bg-gray-600"
    },
    {
      name: "Vimeo",
      key: "vimeo",
      highlight: false,
      price: "$20+/mo",
      description: "Video hosting",
      badge: null,
      color: "bg-purple-600"
    }
  ];

  const renderFeatureValue = (value: boolean | string, isHiveShare: boolean = false) => {
    if (value === true) {
      return (
        <div className={`flex justify-center ${isHiveShare ? 'text-blue-600' : 'text-green-600'}`}>
          <Check className="h-5 w-5" />
        </div>
      );
    }
    if (value === false) {
      return (
        <div className="flex justify-center text-red-500">
          <X className="h-5 w-5" />
        </div>
      );
    }
    return (
      <div className="flex justify-center">
        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
          {value}
        </span>
      </div>
    );
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Not Use [Competitors]?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how HiveShare compares to other platforms. 
            <span className="font-semibold text-gray-800"> Spoiler: We're built specifically for you.</span>
          </p>
        </motion.div>

        {/* Mobile Comparison Cards */}
        <div className="lg:hidden space-y-6 mb-12">
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className={`${platform.highlight ? 'border-2 border-blue-300 shadow-lg' : 'border border-gray-200'}`}>
                <CardHeader className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <CardTitle className="text-xl font-bold">{platform.name}</CardTitle>
                    {platform.badge && (
                      <Badge className="ml-2 bg-blue-100 text-blue-700">
                        <Star className="h-3 w-3 mr-1" />
                        {platform.badge}
                      </Badge>
                    )}
                  </div>
                  <div className={`${platform.color} text-white px-3 py-1 rounded-full inline-block text-sm font-semibold`}>
                    {platform.price}
                  </div>
                  <p className="text-sm text-gray-600">{platform.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {features.slice(0, 5).map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{feature.name}</span>
                        {renderFeatureValue(feature[platform.key as keyof typeof feature], platform.highlight)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Desktop Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="hidden lg:block max-w-6xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="grid grid-cols-5 bg-gray-50 border-b border-gray-200">
              <div className="p-6">
                <h3 className="font-semibold text-gray-900">Features</h3>
              </div>
              {platforms.map((platform) => (
                <div key={platform.key} className={`p-6 text-center ${platform.highlight ? 'bg-blue-50 border-x-2 border-blue-300' : ''}`}>
                  <div className="flex items-center justify-center mb-2">
                    <h4 className="font-bold text-gray-900">{platform.name}</h4>
                    {platform.badge && (
                      <Badge className="ml-2 bg-blue-100 text-blue-700">
                        <Star className="h-3 w-3 mr-1" />
                        {platform.badge}
                      </Badge>
                    )}
                  </div>
                  <div className={`${platform.color} text-white px-3 py-1 rounded-full inline-block text-sm font-semibold mb-1`}>
                    {platform.price}
                  </div>
                  <p className="text-xs text-gray-600">{platform.description}</p>
                </div>
              ))}
            </div>

            {/* Feature Rows */}
            {features.map((feature, index) => (
              <div key={index} className={`grid grid-cols-5 border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <div className="p-4">
                  <span className="text-sm font-medium text-gray-700">{feature.name}</span>
                </div>
                {platforms.map((platform) => (
                  <div key={platform.key} className={`p-4 text-center ${platform.highlight ? 'bg-blue-50 border-x border-blue-200' : ''}`}>
                    {renderFeatureValue(feature[platform.key as keyof typeof feature], platform.highlight)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <X className="h-8 w-8 text-red-600 mx-auto mb-4" />
              <h3 className="font-bold text-red-800 mb-2">YouTube/Band</h3>
              <p className="text-sm text-red-700">Ads, no privacy, messy sharing</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="p-6 text-center">
              <X className="h-8 w-8 text-yellow-600 mx-auto mb-4" />
              <h3 className="font-bold text-yellow-800 mb-2">Dropbox/Vimeo</h3>
              <p className="text-sm text-yellow-700">Not built for families, expensive</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-6 text-center">
              <Shield className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-blue-800 mb-2">HiveShare</h3>
              <p className="text-sm text-blue-700">Secure, simple, built for activity businesses</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonTable;